// Bar replay: rewind the chart to a past moment, hide every candle after it,
// and step forward one candle at a time — practice reading the chart and
// placing trades without seeing the future.
import { RESULT_META, type ChartSignal } from "../chartSignals";
import type { Candle } from "../indicators";

/** Candles loaded before the start (history to read) and after it (to step through). */
export const REPLAY_BEFORE_BARS = 300;
export const REPLAY_AFTER_BARS = 600;
/** The engine's signals come from one 1h backtest per replay: this many days up to its end. */
export const REPLAY_SIGNAL_DAYS = 30;
/** The backtester's entry window (backend ENTRY_TIMEOUT_MS): an entry not reached in 6h is no trade. */
const ENTRY_TIMEOUT_SEC = 6 * 3600;
/** "Random day" picks a start in the year before the latest one that still has a full step-through window. */
const RANDOM_SPAN_SEC = 365 * 86400;
/** At most this long to step through: 600 daily candles would push a random day back past most coins' listing. */
const AFTER_SPAN_SEC = 180 * 86400;

/** Candles revealed per second at each speed. */
export const REPLAY_SPEEDS = [1, 2, 5, 10] as const;
export type ReplaySpeed = (typeof REPLAY_SPEEDS)[number];

/** Bar open time on the timeframe's grid. */
export function alignToBar(sec: number, tfSec: number): number {
  return Math.floor(sec / tfSec) * tfSec;
}

/** Candles loaded after the start on this timeframe: 600, or 180 days' worth on 1d. */
export function replayAfterBars(tfSec: number): number {
  return Math.min(REPLAY_AFTER_BARS, Math.floor(AFTER_SPAN_SEC / tfSec));
}

/**
 * A random replay time: a bar close in the year before the latest start that
 * still leaves a full window (replayAfterBars) to step through. Not knowing
 * the day is the point — you can't remember what came next.
 */
export function randomReplayTime(nowSec: number, tfSec: number, rand: () => number = Math.random): number {
  const latest = alignToBar(nowSec - replayAfterBars(tfSec) * tfSec, tfSec);
  return alignToBar(latest - rand() * RANDOM_SPAN_SEC, tfSec);
}

/**
 * The engine-signal window for a replay anchored at `anchorSec`: one 1h
 * backtest ending 25 days after it (or now), so stepping forward never needs
 * a refetch and switching timeframe reuses it.
 */
export function signalWindow(anchorSec: number, nowSec: number): { end: number; days: number } {
  return { end: Math.min(alignToBar(nowSec, 3600), alignToBar(anchorSec, 3600) + 25 * 86400), days: REPLAY_SIGNAL_DAYS };
}

/**
 * A drawing belongs in a replay at `nowSec` once its first anchor is in the
 * past: one drawn on later candles would show the future. Lines with no time
 * (horizontal) always show.
 */
export function drawnBy(d: { time?: number; time2?: number; time3?: number }, nowSec: number): boolean {
  const times = [d.time, d.time2, d.time3].filter((t): t is number => t != null);
  return times.length === 0 || Math.min(...times) <= nowSec;
}

/** The candles visible at replay time `nowSec`: those that had CLOSED by then. */
export function visibleAt(candles: Candle[], nowSec: number, tfSec: number): Candle[] {
  let n = 0;
  while (n < candles.length && candles[n]!.time + tfSec <= nowSec) n++;
  return n === candles.length ? candles : candles.slice(0, n);
}

/** Replay time after stepping `bars` candles from `nowSec` (stays on the bar grid). */
export function stepTime(nowSec: number, tfSec: number, bars = 1): number {
  return alignToBar(nowSec, tfSec) + bars * tfSec;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/** R of a price against a plan: + in the trade's favour. */
export function rAt(action: "LONG" | "SHORT", entry: number, stop: number, price: number): number {
  const risk = Math.abs(entry - stop);
  if (!(risk > 0)) return 0;
  return round2(((action === "SHORT" ? -1 : 1) * (price - entry)) / risk);
}

/**
 * A signal as it looked at replay time `nowSec`: hidden until it fired, then
 * waiting / open / done only as far as replay time has reached. `lastClose`
 * marks an open trade to the last visible close.
 */
export function signalAt(s: ChartSignal, nowSec: number, lastClose: number | null): ChartSignal | null {
  if (s.signalTime > nowSec) return null;
  const blank = { tp1Time: null, exitTime: null, exitPrice: null, r: null };
  if (s.fillTime === null || s.fillTime > nowSec) {
    // Never filled and the entry window has run out by now: no trade.
    if (s.fillTime === null && s.result === "no-fill" && s.signalTime + ENTRY_TIMEOUT_SEC <= nowSec) return s;
    return { ...s, ...blank, result: "pending", fillTime: null };
  }
  if (s.exitTime !== null && s.exitTime <= nowSec && RESULT_META[s.result].done) return s;
  return {
    ...s,
    ...blank,
    result: "open",
    tp1Time: s.tp1Time !== null && s.tp1Time <= nowSec ? s.tp1Time : null,
    r: lastClose === null ? null : rAt(s.action, s.entry, s.stopLoss, lastClose),
  };
}

// ── Practice trades ─────────────────────────────────────────────────────────

export interface PracticeTrade {
  id: string;
  side: "LONG" | "SHORT";
  entry: number;
  stop: number;
  target: number;
  /** Replay time it was placed: the close of the last visible candle. */
  openSec: number;
  /** `sec`: open time of the candle it closed in (manual: the replay time). */
  exit: { sec: number; price: number; reason: "target" | "stop" | "manual" } | null;
}

/**
 * Close a practice trade on the first candle after its entry that reaches
 * the stop or the target. A candle that reaches both counts as the stop: a
 * candle doesn't tell which came first, so assume the worse. A candle that
 * opens past the stop fills at its open (a gap).
 */
export function settle(t: PracticeTrade, candles: Candle[]): PracticeTrade {
  if (t.exit) return t;
  const long = t.side === "LONG";
  for (const c of candles) {
    if (c.time < t.openSec) continue; // candles that opened before the entry
    const hitStop = long ? c.low <= t.stop : c.high >= t.stop;
    if (hitStop) {
      const price = long ? Math.min(t.stop, c.open) : Math.max(t.stop, c.open);
      return { ...t, exit: { sec: c.time, price, reason: "stop" } };
    }
    const hitTarget = long ? c.high >= t.target : c.low <= t.target;
    if (hitTarget) return { ...t, exit: { sec: c.time, price: t.target, reason: "target" } };
  }
  return t;
}

/** The trade's R: realised once closed, else at the last visible close. */
export function practiceR(t: PracticeTrade, lastClose: number | null): number | null {
  const price = t.exit?.price ?? lastClose;
  return price === null ? null : rAt(t.side, t.entry, t.stop, price);
}

/** A practice trade drawn like the trader's own trades ("YOU BUY"). */
export function practiceSignal(t: PracticeTrade, lastClose: number | null): ChartSignal {
  const result: ChartSignal["result"] = !t.exit
    ? "open"
    : t.exit.reason === "target" ? "full-win" : t.exit.reason === "stop" ? "stopped" : "closed";
  return {
    id: `practice-${t.id}`,
    source: "mine",
    action: t.side,
    signalTime: t.openSec,
    entry: t.entry,
    stopLoss: t.stop,
    tp1: t.target,
    tp2: t.target,
    riskReward: round2(Math.abs(t.target - t.entry) / (Math.abs(t.entry - t.stop) || 1)),
    archetype: null, quality: null, trigger: null, thesis: null, score: null, timeStopHours: null,
    result,
    fillTime: t.openSec,
    tp1Time: null,
    exitTime: t.exit?.sec ?? null,
    exitPrice: t.exit?.price ?? null,
    r: practiceR(t, lastClose),
  };
}

/** Average true range of the last `period` candles (simple mean). */
export function atr(candles: Candle[], period = 14): number | null {
  if (candles.length < 2) return null;
  const from = Math.max(1, candles.length - period);
  let sum = 0;
  for (let i = from; i < candles.length; i++) {
    const c = candles[i]!;
    const prev = candles[i - 1]!.close;
    sum += Math.max(c.high - c.low, Math.abs(c.high - prev), Math.abs(c.low - prev));
  }
  return sum / (candles.length - from);
}

/** A starting plan for a practice trade: stop 1 ATR away, target 2× the risk. */
export function defaultPlan(side: "LONG" | "SHORT", entry: number, atrValue: number | null): { stop: number; target: number } {
  const risk = atrValue && atrValue > 0 ? atrValue : entry * 0.01;
  const dir = side === "LONG" ? 1 : -1;
  return { stop: entry - dir * risk, target: entry + dir * 2 * risk };
}

/** What's wrong with a plan, in plain words; null when it's usable. */
export function planProblem(side: "LONG" | "SHORT", entry: number, stop: number, target: number): string | null {
  if (!(stop > 0) || !(target > 0)) return "Enter a stop and a target.";
  if (side === "LONG" && stop >= entry) return "A buy's stop goes below the entry.";
  if (side === "SHORT" && stop <= entry) return "A sell's stop goes above the entry.";
  if (side === "LONG" && target <= entry) return "A buy's target goes above the entry.";
  if (side === "SHORT" && target >= entry) return "A sell's target goes below the entry.";
  return null;
}

export interface Scorecard {
  closed: number;
  won: number;
  lost: number;
  open: number;
  totalR: number;
}

export function scorecard(trades: PracticeTrade[]): Scorecard {
  const card: Scorecard = { closed: 0, won: 0, lost: 0, open: 0, totalR: 0 };
  for (const t of trades) {
    if (!t.exit) {
      card.open++;
      continue;
    }
    const r = practiceR(t, null) ?? 0;
    card.closed++;
    card.totalR = round2(card.totalR + r);
    if (r > 0.05) card.won++;
    else if (r < -0.05) card.lost++;
  }
  return card;
}

// ── Lifetime practice record (this browser) ─────────────────────────────────

const RECORD_KEY = "vp.replay.record";

export interface PracticeRecord {
  trades: number;
  won: number;
  totalR: number;
}

export function readRecord(): PracticeRecord {
  try {
    const v = JSON.parse(localStorage.getItem(RECORD_KEY) ?? "null");
    if (v && Number.isFinite(v.trades) && Number.isFinite(v.won) && Number.isFinite(v.totalR)) return v;
  } catch {
    /* fall through */
  }
  return { trades: 0, won: 0, totalR: 0 };
}

/** Adds closed practice trades to the running record; returns the new record. */
export function addToRecord(closed: PracticeTrade[], nowMs = Date.now()): PracticeRecord {
  const rec = readRecord();
  const log = readReplayLog();
  for (const t of closed) {
    const r = practiceR(t, null) ?? 0;
    rec.trades++;
    if (r > 0.05) rec.won++;
    rec.totalR = round2(rec.totalR + r);
    log.push({ at: nowMs, r: round2(r) });
  }
  try {
    localStorage.setItem(RECORD_KEY, JSON.stringify(rec));
    localStorage.setItem(LOG_KEY, JSON.stringify(log.slice(-LOG_MAX)));
  } catch {
    /* storage blocked */
  }
  if (closed.length > 0 && typeof window !== "undefined") window.dispatchEvent(new Event(REPLAY_RECORDED));
  return rec;
}

/**
 * Each closed replay trade with the real time it closed (not the replay's):
 * the 30-day plan counts practice by day. Only the latest LOG_MAX are kept.
 */
export interface ReplayLogEntry { at: number; r: number }
const LOG_KEY = "vp.replay.log";
const LOG_MAX = 1000;
/** Window event after a replay trade is added to the record. */
export const REPLAY_RECORDED = "vp:replay-recorded";

export function readReplayLog(): ReplayLogEntry[] {
  try {
    const v: unknown = JSON.parse(localStorage.getItem(LOG_KEY) ?? "[]");
    return Array.isArray(v) ? v.filter((e): e is ReplayLogEntry => Number.isFinite(e?.at) && Number.isFinite(e?.r)) : [];
  } catch {
    return [];
  }
}
